import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  downloadCertificate,
  fetchCertificates,
  fetchProductCertificates,
  linkProductCertificate,
  unlinkProductCertificate,
} from '../../api/certificate';
import { Certificate, certificateValidityLabel, formatCertPeriod, formatCertScopes, formatFileSize } from '../../types/product';
import '../equipment/EquipTabPanel.css';

import { reportError } from '../../utils/notify';
interface Props {
  productId: number;
}

function ProductCertificatesTab({ productId }: Props) {
  const [linked, setLinked] = useState<Certificate[]>([]);
  const [catalog, setCatalog] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState<number | ''>('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [linkedRows, all] = await Promise.all([
        fetchProductCertificates(productId),
        fetchCertificates(),
      ]);
      setLinked(linkedRows);
      setCatalog(all);
      setError('');
    } catch {
      reportError(setError, '인증서를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    void load();
  }, [load]);

  const linkedIds = useMemo(
    () => new Set(linked.map((row) => row.certificateId)),
    [linked]
  );

  const available = catalog.filter((item) => !linkedIds.has(item.certificateId));

  const openCreate = () => {
    setSelectedId(available[0]?.certificateId ?? '');
    setShowForm(true);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (selectedId === '') {
      reportError(setError, '연결할 인증서를 선택해 주세요.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      await linkProductCertificate(productId, Number(selectedId));
      setShowForm(false);
      await load();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '연결에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (row: Certificate) => {
    try {
      await downloadCertificate(row.certificateId, row.fileName || 'file');
    } catch {
      reportError(setError, '파일 다운로드에 실패했습니다.');
    }
  };

  const handleUnlink = async (row: Certificate) => {
    if (!window.confirm(`'${row.title}' 연결을 해제할까요?\n인증서 자체는 삭제되지 않습니다.`)) {
      return;
    }
    try {
      await unlinkProductCertificate(productId, row.certificateId);
      await load();
    } catch {
      reportError(setError, '연결 해제에 실패했습니다.');
    }
  };

  return (
    <div className="equip-tab-panel">
      <div className="equip-tab-panel__toolbar">
        <div>
          <h3>인증서</h3>
          <p className="equip-tab-panel__hint">
            상단 제품 → 인증서에서 등록한 인증서를 이 제품에 연결합니다.
          </p>
        </div>
        <button type="button" onClick={openCreate} disabled={available.length === 0 && !showForm}>
          연결
        </button>
      </div>

      {error && <p className="equip-tab-panel__error">{error}</p>}

      {showForm && (
        <form className="equip-tab-panel__form" onSubmit={(e) => void handleSubmit(e)}>
          <label className="is-span-2">
            인증서 선택 *
            <select
              value={selectedId}
              onChange={(e) =>
                setSelectedId(e.target.value ? Number(e.target.value) : '')
              }
              required
            >
              <option value="">선택</option>
              {available.map((item) => (
                <option key={item.certificateId} value={item.certificateId}>
                  {item.title}
                  {item.validTo ? ` · ~${item.validTo.slice(0, 10)}` : ''}
                  {item.fileName ? ` · ${item.fileName}` : ''}
                </option>
              ))}
            </select>
          </label>
          {available.length === 0 && (
            <p className="equip-tab-panel__hint" style={{ gridColumn: '1 / -1', margin: 0 }}>
              연결 가능한 인증서가 없습니다. 제품 → 인증서 메뉴에서 먼저 등록해 주세요.
            </p>
          )}
          <div className="equip-tab-panel__form-actions">
            <button type="button" className="is-ghost" onClick={() => setShowForm(false)}>
              취소
            </button>
            <button type="submit" disabled={saving || available.length === 0}>
              {saving ? '연결 중...' : '연결'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="equip-tab-panel__hint">불러오는 중...</p>
      ) : linked.length === 0 ? (
        <p className="equip-tab-panel__hint">연결된 인증서가 없습니다.</p>
      ) : (
        <table className="equip-tab-panel__table">
          <thead>
            <tr>
              <th style={{ width: '48px' }}>No</th>
              <th style={{ width: '140px' }}>인증명</th>
              <th>인증범위</th>
              <th style={{ width: '160px' }}>유효기간</th>
              <th style={{ width: '140px' }}>파일</th>
              <th style={{ width: '140px' }} />
            </tr>
          </thead>
          <tbody>
            {linked.map((row, index) => (
              <tr key={row.certificateId}>
                <td>{index + 1}</td>
                <td>
                  {row.title}
                  {row.validityStatus && row.validityStatus !== 'OK' ? (
                    <span
                      className={`equip-calib-status is-${row.validityStatus.toLowerCase()}`}
                      style={{ marginLeft: 6 }}
                    >
                      {certificateValidityLabel(row.validityStatus)}
                    </span>
                  ) : null}
                </td>
                <td>{formatCertScopes(row.scopes)}</td>
                <td>{formatCertPeriod(row.validFrom, row.validTo)}</td>
                <td>
                  {row.fileName}
                  {row.fileSize != null ? ` (${formatFileSize(row.fileSize)})` : ''}
                </td>
                <td>
                  <div className="equip-tab-panel__row-actions">
                    <button
                      type="button"
                      className="is-ghost"
                      onClick={() => void handleDownload(row)}
                    >
                      다운로드
                    </button>
                    <button
                      type="button"
                      className="is-danger"
                      onClick={() => void handleUnlink(row)}
                    >
                      해제
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default ProductCertificatesTab;
